import {
    getLangObjects,
    LanguageCode,
    LoggedPage,
    NativeLanguageDTO,
    PrepCheckQuestionSubmission,
    PrepCheckStatus,
    PrepCheckUserSubmissionDTO,
} from '@hazadapt-git/public-core-base'
import { Error as ErrorIcon } from '@mui/icons-material'
import React, { FC } from 'react'
import qs from 'query-string'
import { useLocation, useNavigate } from 'react-router-dom'
import voca from 'voca'

import {
    PrepCheckCardDropItemProps,
    PrepCheckPageTemplate,
    ZipCodeAlert,
} from '../components'
import { PageProps, GuestStorageError } from '../lib/entities'
import {
    getPrepCheckThunk,
    getPrepCheckUserStatsThunk,
    saveProfileChangesThunk,
    selectActivePrepCheck,
    selectIsAppReady,
    selectLanguage,
    selectPrepCheckUserStats,
    selectSupportedLanguages,
    selectUser,
    switchLanguageThunk,
} from '../lib/slices'
import { useAppDispatch, useAppSelector } from '../lib/store'
import {
    errorColor,
    primaryIconSize,
    successColor,
} from '../lib/styles/universal'
import {
    getGuestZip,
    logEvent,
    shouldShowZipAlert,
    toast,
    getEmergencyItemsForPrepCheck,
    getOneDraftSubmission,
    savePrepCheckSubmission,
    getGuestPrepCheckSubmission,
    getGuestTutorialsShown,
    markGuestTutorialShown,
    saveGuestPrepCheckSubmission,
    submitGuestPrepCheck,
    canGuestAccessPrepCheck,
    signup,
    getAllPrepCheckResults,
    getGuestResultSummaries,
} from '../lib/utils'
import { getSessionItem, setSessionItem } from '../lib/browser-storage'
import { IoCloudDownloadOutline, IoLanguage, IoWarning } from 'react-icons/io5'
import { shallowEqual } from 'react-redux'
import { getEnvVars } from '../lib/config'

interface PrepCheckPageProps extends PageProps {}

const env = getEnvVars()
export const PrepCheckPage: FC<PrepCheckPageProps> = (
    props: PrepCheckPageProps
) => {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const navigate = useNavigate()

    // NOTE: This only becomes true when prep check retrieval dispatch has completed.
    // This makes sure the user only sees the current prep check on this page.
    const loadedSubmission = React.useRef<boolean>(false)

    const language = useAppSelector(selectLanguage)
    const user = useAppSelector(selectUser, shallowEqual)
    const appReady = useAppSelector(selectIsAppReady)
    const prepCheck = useAppSelector(selectActivePrepCheck, shallowEqual)
    const stats = useAppSelector(selectPrepCheckUserStats, shallowEqual)
    const supportedLanguages = useAppSelector(
        selectSupportedLanguages,
        shallowEqual
    )
    const [activeQuestionIdx, setActiveQuestionIdx] = React.useState<number>(0)
    const [draftSubmission, setDraftSubmission] =
        React.useState<PrepCheckUserSubmissionDTO>()
    const [answerConfig, setAnswerConfig] = React.useState<
        PrepCheckCardDropItemProps[]
    >([])
    const [
        myRelevantEmergencyItemAnswerIds,
        setMyRelevantEmergencyItemAnswerIds,
    ] = React.useState<number[]>([])
    const [savingInProgress, setSavingInProgress] =
        React.useState<boolean>(false)
    const [savedSuccessfully, setSavedSuccessfully] =
        React.useState<boolean>(true)
    const [languageSelectorModalOpen, setLanguageSelectorModalOpen] =
        React.useState<boolean>(false)
    const [showZipAlert, setShowZipAlert] = React.useState(false)
    const [guestTutorialsShown, setGuestTutorialsShown] = React.useState<
        string[]
    >([])
    const [showWelcomeModal, setShowWelcomeModal] =
        React.useState<boolean>(false)
    const [showPrepCheckLimitWarning, setShowPrepCheckLimitWarning] =
        React.useState<boolean>(false)

    React.useEffect(() => {
        // Initialize guest tutorials shown state and check access limit
        if (!user && prepCheck) {
            setGuestTutorialsShown(getGuestTutorialsShown())
            if (!canGuestAccessPrepCheck(prepCheck.id)) {
                setShowPrepCheckLimitWarning(true)
            }
        }
    }, [user, prepCheck])

    // Show welcome modal, once for auth users and once per session for guests
    React.useEffect(() => {
        if (appReady) {
            const hasSeenModal = user
                ? user.tutorials_shown.includes('PrepCheckWelcome')
                : getSessionItem('hazadapt_guest_welcome_seen')
            if (!hasSeenModal) {
                setShowWelcomeModal(true)
            }
        }
    }, [appReady, user])

    React.useEffect(() => {
        if (!appReady) return

        const urlPieces = location.pathname.split('/')
        const identifier = urlPieces[urlPieces.length - 1]
        if (voca.isNumeric(identifier)) {
            // Get data for prep check
            dispatch(getPrepCheckUserStatsThunk(language))
            dispatch(
                getPrepCheckThunk({ id: parseInt(identifier), language })
            ).catch((err) => {
                console.error(err)
                navigate('/prep-checks')
            })
            // Load draft submission (from API for logged-in users, localStorage for guests)
            if (user) {
                getOneDraftSubmission(parseInt(identifier), language)
                    .then((sub) => {
                        setDraftSubmission(sub)
                        setActiveQuestionIdx(sub.submitted_responses.length - 1)
                    })
                    .catch((err) => {
                        console.error(err)
                        setActiveQuestionIdx(0)
                        loadedSubmission.current = true
                    })
            } else {
                // Guest user - check localStorage (will be processed in separate effect once prepCheck loads)
                loadedSubmission.current = true
            }
        }
    }, [location.pathname, language, dispatch, appReady, navigate, user])

    // Separate effect to handle guest submission loading once prepCheck is available
    React.useEffect(() => {
        if (!user && prepCheck && !draftSubmission) {
            const urlPieces = location.pathname.split('/')
            const identifier = urlPieces[urlPieces.length - 1]
            if (voca.isNumeric(identifier)) {
                const prepCheckId = parseInt(identifier)
                const guestSubmission = getGuestPrepCheckSubmission(
                    prepCheckId
                ) as
                    | (PrepCheckUserSubmissionDTO & {
                          total_questions?: number
                      })
                    | null

                if (
                    guestSubmission &&
                    guestSubmission.prep_check_id === prepCheckId
                ) {
                    // Ensure total_questions is set for progress calculation
                    const submissionWithMetadata = {
                        ...guestSubmission,
                        total_questions: prepCheck.questions.length,
                    }
                    setDraftSubmission(submissionWithMetadata)

                    // If total_questions was missing, save it back to localStorage immediately
                    if (!guestSubmission.total_questions) {
                        saveGuestPrepCheckSubmission(submissionWithMetadata)
                    }

                    setActiveQuestionIdx(
                        guestSubmission.submitted_responses.length - 1
                    )
                } else {
                    setActiveQuestionIdx(0)
                }
            }
        }
    }, [user, prepCheck, draftSubmission, location.pathname])

    // Check if prep check has already been completed; redirect to results if so
    React.useEffect(() => {
        if (!prepCheck) return

        const checkCompletion = async () => {
            let results: any[] = []
            if (user) {
                try {
                    results = await getAllPrepCheckResults()
                } catch (err) {
                    console.error('Failed to fetch results:', err)
                    return
                }
            } else {
                results = getGuestResultSummaries()
            }

            const result = results.find((r) => r.prep_check_id === prepCheck.id)
            if (result) {
                navigate(`/prep-checks/${prepCheck.id}/results`)
            }
        }

        checkCompletion()
    }, [prepCheck, user, navigate])

    React.useEffect(() => {
        if (!appReady) return

        // Page setup
        if (prepCheck) {
            document.title = `${
                prepCheck.title.includes('Prep Check')
                    ? prepCheck.title
                    : `${prepCheck.title} Prep Check`
            } - HazAdapt`
            const searchParams = qs.parse(location.search)
            const fromPath = searchParams.from
            logEvent('OPEN_PAGE', {
                page: LoggedPage.PREP_CHECK,
                language,
                from: Array.isArray(fromPath) ? fromPath[0] : fromPath,
                prep_check_id: prepCheck.id,
            })
        }
    }, [language, prepCheck, appReady, location.search])

    React.useEffect(() => {
        // Save prep check submission when it's updated
        if (!loadedSubmission.current) {
            loadedSubmission.current = true
        } else if (
            draftSubmission &&
            draftSubmission.submitted_responses.length > 0
        ) {
            setSavingInProgress(true)

            // Save to API for logged-in users, localStorage for guests
            const saveOperation = user
                ? savePrepCheckSubmission({
                      ...draftSubmission,
                      status: PrepCheckStatus.DRAFT,
                  })
                : Promise.resolve(
                      saveGuestPrepCheckSubmission({
                          ...draftSubmission,
                          status: PrepCheckStatus.DRAFT,
                      })
                  ).then((result) => {
                      if (!result.success) {
                          // Show user-friendly error message based on error type
                          if (
                              result.error === GuestStorageError.QUOTA_EXCEEDED
                          ) {
                              toast(
                                  result.errorMessage ||
                                      'Storage is full. Please create an account to continue.',
                                  <IoWarning
                                      color={errorColor}
                                      size={primaryIconSize}
                                  />
                              )
                          } else if (
                              result.error ===
                              GuestStorageError.STORAGE_UNAVAILABLE
                          ) {
                              toast(
                                  result.errorMessage ||
                                      'Browser storage is not available. Progress cannot be saved.',
                                  <IoWarning
                                      color={errorColor}
                                      size={primaryIconSize}
                                  />
                              )
                          } else {
                              toast(
                                  result.errorMessage ||
                                      'Failed to save progress.',
                                  <IoWarning
                                      color={errorColor}
                                      size={primaryIconSize}
                                  />
                              )
                          }
                          throw new Error(
                              result.errorMessage ||
                                  'Failed to save to localStorage'
                          )
                      }
                  })

            saveOperation
                .then(() => {
                    setSavedSuccessfully(true)
                })
                .catch(() => {
                    setSavedSuccessfully(false)
                })
                .finally(() => {
                    setSavingInProgress(false)
                })
        }
    }, [draftSubmission, user])

    // Get the emergency items the user has for the prep check
    React.useEffect(() => {
        if (!prepCheck) return
        getEmergencyItemsForPrepCheck(prepCheck.id)
            .then(setMyRelevantEmergencyItemAnswerIds)
            .catch(console.error)
    }, [prepCheck])

    React.useEffect(() => {
        // Build answer config object (used for user_answers)
        if (!prepCheck) return
        try {
            const question = prepCheck.questions[activeQuestionIdx]
            if (question.question_type === 'Checkboxes') {
                // answerConfig is PrepCheckCardDropItemProps[], which is not applicable to checkbox-type questions
                setAnswerConfig([])
            } else if (question.question_type === 'TwoD_Grid_Placement') {
                const beforeColumnSize = question.options.filter(
                    (o) => o.best_stage === 'Before'
                ).length
                const duringColumnSize = question.options.filter(
                    (o) => o.best_stage === 'During'
                ).length
                const config: PrepCheckCardDropItemProps[] = []
                try {
                    // Build answer config from existing submission
                    if (!draftSubmission) throw new Error('No draft submission')
                    const response = draftSubmission.submitted_responses.find(
                        (sr) =>
                            sr.question_id ===
                            prepCheck.questions[activeQuestionIdx].id
                    )
                    if (!response)
                        throw new Error('No draft submission for this question')

                    const iter = [...Array(question.options.length)]
                    iter.forEach((_, index) => {
                        const userAnswer = response.user_answer.find(
                            (ua) =>
                                'placement' in ua.answer &&
                                ((ua.answer.placement[0] === 'Before' &&
                                    index < beforeColumnSize &&
                                    index === ua.answer.placement[1] - 1) ||
                                    (ua.answer.placement[0] === 'During' &&
                                        index >= beforeColumnSize &&
                                        index <
                                            beforeColumnSize +
                                                duringColumnSize &&
                                        index - beforeColumnSize ===
                                            ua.answer.placement[1] - 1) ||
                                    (ua.answer.placement[0] === 'After' &&
                                        index >=
                                            beforeColumnSize +
                                                duringColumnSize &&
                                        index -
                                            (beforeColumnSize +
                                                duringColumnSize) ===
                                            ua.answer.placement[1] - 1))
                        )
                        const placedAnswer = userAnswer
                            ? question.options.find(
                                  (o) => o.id === userAnswer.answer_id
                              )
                            : undefined
                        config.push({
                            index,
                            active: false,
                            questionType: 'TwoD_Grid_Placement',
                            priority:
                                (index < beforeColumnSize
                                    ? index
                                    : index <
                                      beforeColumnSize + duringColumnSize
                                    ? index - beforeColumnSize
                                    : index -
                                      (beforeColumnSize + duringColumnSize)) +
                                1,
                            prepStage:
                                index < beforeColumnSize
                                    ? 'Before'
                                    : index <
                                      beforeColumnSize + duringColumnSize
                                    ? 'During'
                                    : 'After',
                            answerOption: placedAnswer,
                        })
                    })
                } catch (err) {
                    // Build answer config from scratch
                    const iter = [...Array(question.options.length)]
                    iter.forEach((_, index) => {
                        config.push({
                            index,
                            active: false,
                            questionType: 'TwoD_Grid_Placement',
                            priority:
                                (index < beforeColumnSize
                                    ? index
                                    : index <
                                      beforeColumnSize + duringColumnSize
                                    ? index - beforeColumnSize
                                    : index -
                                      (beforeColumnSize + duringColumnSize)) +
                                1,
                            prepStage:
                                index < beforeColumnSize
                                    ? 'Before'
                                    : index <
                                      beforeColumnSize + duringColumnSize
                                    ? 'During'
                                    : 'After',
                            answerOption: undefined,
                        })
                    })
                }
                setAnswerConfig(config)
            } else {
                const config: PrepCheckCardDropItemProps[] = []
                try {
                    // Build answer config from existing submission
                    if (!draftSubmission) throw new Error('No draft submission')
                    const response = draftSubmission.submitted_responses.find(
                        (sr) =>
                            sr.question_id ===
                            prepCheck.questions[activeQuestionIdx].id
                    )
                    if (!response)
                        throw new Error('No draft submission for this question')

                    question.options.forEach((_, index) => {
                        const userAnswer = response.user_answer.find(
                            (ua) =>
                                'order' in ua.answer &&
                                ua.answer.order === index + 1
                        )
                        const placedAnswer = userAnswer
                            ? question.options.find(
                                  (o) => o.id === userAnswer.answer_id
                              )
                            : undefined
                        config.push({
                            index,
                            active: index === response.user_answer.length,
                            questionType: 'OneD_Ordered_List',
                            priority: index + 1,
                            answerOption: placedAnswer,
                        })
                    })
                    setAnswerConfig(config)
                } catch (err) {
                    // Build answer config from scratch
                    question.options.forEach((_, index) => {
                        config.push({
                            index,
                            active: index === 0,
                            questionType: 'OneD_Ordered_List',
                            priority: index + 1,
                            answerOption: undefined,
                        })
                    })
                    setAnswerConfig(config)
                }
            }
        } catch (err) {
            console.error(err)
        }
    }, [activeQuestionIdx, prepCheck, draftSubmission])

    React.useEffect(() => {
        window.scrollTo(0, 0)
    }, [activeQuestionIdx])

    const onPreviousQuestionClick = () => {
        setActiveQuestionIdx((idx) => Math.max(0, idx - 1))
    }

    const onNextQuestionClick = () => {
        if (!prepCheck) return

        // Get current question
        const activeQuestion = prepCheck.questions.at(activeQuestionIdx)
        if (!activeQuestion) return

        const newActiveQuestionIdx = Math.min(
            prepCheck.questions.length - 1,
            activeQuestionIdx + 1
        )
        if (
            newActiveQuestionIdx < 0 ||
            newActiveQuestionIdx >= prepCheck.questions.length
        )
            return
        const newActiveQuestion = prepCheck.questions[newActiveQuestionIdx]

        // Get current draft submission, initializing a new one if it doesn't already exist
        let draft_submission: PrepCheckUserSubmissionDTO & {
            total_questions?: number
        }
        if (draftSubmission) {
            draft_submission = {
                ...draftSubmission,
                // Ensure total_questions is always set for progress calculation
                total_questions: prepCheck.questions.length,
            }
        } else {
            draft_submission = {
                prep_check_id: prepCheck.id,
                status: PrepCheckStatus.DRAFT,
                submitted_responses: [
                    {
                        question_id: activeQuestion.id,
                        user_answer: [],
                    },
                ],
                // Store total questions for progress calculation
                total_questions: prepCheck.questions.length,
            }
        }

        // Initialize next question's user answer list and save so that on next refresh,
        // the user would be auto-dropped into this next question.
        const responseAlreadyExists = draft_submission.submitted_responses.some(
            (sr) => sr.question_id === newActiveQuestion.id
        )

        if (!responseAlreadyExists) {
            if (newActiveQuestion.link_to_emergency_items) {
                // Build with emergency items
                const response: PrepCheckQuestionSubmission = {
                    question_id: newActiveQuestion.id,
                    user_answer: [],
                }
                for (const answer_id of myRelevantEmergencyItemAnswerIds) {
                    const inQuestion = newActiveQuestion.options.some(
                        (o) => o.id === answer_id
                    )
                    if (!inQuestion) continue

                    response.user_answer.push({
                        answer_id,
                        answer: {
                            id: answer_id,
                            checked: true,
                        },
                    })
                }
                draft_submission.submitted_responses.push(response)
            } else {
                draft_submission.submitted_responses.push({
                    question_id: newActiveQuestion.id,
                    user_answer: [],
                })
            }
            setDraftSubmission(draft_submission)
        }

        setActiveQuestionIdx(newActiveQuestionIdx)
    }

    const submitPrepCheck = async () => {
        if (!prepCheck || !draftSubmission) return
        try {
            const submissionData = {
                ...draftSubmission,
                status: PrepCheckStatus.SUBMITTED,
            }

            if (user) {
                // Authenticated user: use auth submission endpoint
                await savePrepCheckSubmission(submissionData)
            } else {
                // Guest: submit for scoring and save results locally
                await submitGuestPrepCheck(submissionData)
            }

            // Navigate to completion page
            const urlPieces = location.pathname.split('/').slice(1)
            const identifier = urlPieces.at(-1)
            if (identifier && voca.isNumeric(identifier)) {
                navigate(`/prep-checks/${parseInt(identifier)}/complete`)
            }
        } catch (err) {
            console.error(err)
            toast(
                'There was an error saving your prep check submission.',
                <ErrorIcon
                    htmlColor={errorColor}
                    sx={{ fontSize: primaryIconSize }}
                />
            )
        }
    }

    const onDoneClick = async (): Promise<void> => {
        // Check if ZIP is needed (for logged-in users with no ZIP or guests without stored ZIP)
        if (
            (user && shouldShowZipAlert(user?.zip)) ||
            (!user && !getGuestZip())
        ) {
            setShowZipAlert(true)
            return
        }

        await submitPrepCheck()
    }

    const handleZipSubmit = async () => {
        setShowZipAlert(false)
        await submitPrepCheck()
    }

    const handleZipClose = () => {
        setShowZipAlert(false)
    }

    const shouldForceShowExplainer = (): boolean => {
        if (!prepCheck) return false

        const tutorialsShown = user ? user.tutorials_shown : guestTutorialsShown

        switch (prepCheck.questions[activeQuestionIdx].question_type) {
            case 'TwoD_Grid_Placement': {
                return !tutorialsShown.includes('TwoD_Grid_Placement')
            }
            case 'Checkboxes': {
                return !tutorialsShown.includes('Checkboxes')
            }
            case 'OneD_Ordered_List': {
                return !tutorialsShown.includes('OneD_Ordered_List')
            }
            default: {
                return false
            }
        }
    }

    const onQuestionExplainerClose = () => {
        if (!prepCheck) return

        const questionType =
            prepCheck.questions[activeQuestionIdx].question_type
        const tutorialName =
            questionType === 'TwoD_Grid_Placement'
                ? 'TwoD_Grid_Placement'
                : questionType === 'Checkboxes'
                ? 'Checkboxes'
                : questionType === 'OneD_Ordered_List'
                ? 'OneD_Ordered_List'
                : null

        if (!tutorialName) return

        if (user) {
            // Logged-in user: save to profile
            const tutorials_shown = new Set<string>(user.tutorials_shown)
            tutorials_shown.add(tutorialName)
            dispatch(
                saveProfileChangesThunk({
                    tutorials_shown: Array.from(tutorials_shown),
                })
            )
        } else {
            // Guest user: save to localStorage and update state
            markGuestTutorialShown(tutorialName)
            setGuestTutorialsShown((prev) => [...prev, tutorialName])
        }
    }

    const onPrepCheckWelcomeClose = () => {
        if (user) {
            const tutorials_shown = new Set<string>(user.tutorials_shown)
            tutorials_shown.add('PrepCheckWelcome')
            dispatch(
                saveProfileChangesThunk({
                    tutorials_shown: Array.from(tutorials_shown),
                })
            )
        } else {
            setShowWelcomeModal(false)
            markGuestTutorialShown('PrepCheckWelcome')
            setSessionItem('hazadapt_guest_welcome_seen', 'true')
        }
    }

    const onPrepCheckLimitWarningModalClose = () => {
        navigate('/prep-checks')
    }

    const onSignUpClick = async () => {
        const redirectUrl = prepCheck?.id
            ? `${env.frontendUriBase}/prep-checks/${prepCheck?.id}`
            : `${env.frontendUriBase}/prep-checks`
        await signup(redirectUrl)
    }

    const onLanguageChange = async (language: LanguageCode) => {
        if (!prepCheck) return
        const langObject: NativeLanguageDTO = getLangObjects([language])[0]
        setLanguageSelectorModalOpen(false)
        toast(
            `Downloading Prep Check in ${langObject.title}.`,
            <IoCloudDownloadOutline
                color={successColor}
                size={primaryIconSize}
            />
        )
        try {
            await dispatch(switchLanguageThunk({ lang: language }))
            await dispatch(getPrepCheckThunk({ id: prepCheck.id, language }))
            toast(
                `Prep Check has been translated into ${langObject.title}.`,
                <IoLanguage color={successColor} size={primaryIconSize} />
            )
        } catch (err) {
            console.error(err)
            toast(
                `Unable to download Prep Check in ${langObject.title}.`,
                <IoLanguage color={errorColor} size={primaryIconSize} />
            )
        }
    }

    const onTranslateClick: React.MouseEventHandler = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setLanguageSelectorModalOpen(true)
    }

    const onLanguageSelectorModalClose = () => {
        setLanguageSelectorModalOpen(false)
    }

    return (
        <>
            {prepCheck &&
            loadedSubmission.current &&
            activeQuestionIdx < prepCheck.questions.length ? (
                <PrepCheckPageTemplate
                    prepCheckId={prepCheck.id}
                    name={
                        prepCheck.title.includes('Prep Check')
                            ? prepCheck.title
                            : `${prepCheck.title} Prep Check`
                    }
                    icon={prepCheck.light_icon}
                    userStats={stats}
                    language={language}
                    submission={draftSubmission}
                    userAnswers={answerConfig}
                    question={prepCheck.questions[activeQuestionIdx]}
                    questionNumber={activeQuestionIdx + 1}
                    totalQuestions={prepCheck.questions.length}
                    onPreviousQuestionClick={onPreviousQuestionClick}
                    onNextQuestionClick={onNextQuestionClick}
                    onDoneClick={onDoneClick}
                    updateSubmission={setDraftSubmission}
                    showTutorial={shouldForceShowExplainer()}
                    onQuestionExplainerClose={onQuestionExplainerClose}
                    savingInProgress={savingInProgress}
                    savedSuccessfully={savedSuccessfully}
                    loading={props.loading}
                    languages={supportedLanguages}
                    selectedLanguage={language}
                    onLanguageChange={onLanguageChange}
                    onLanguageSelectorModalClose={onLanguageSelectorModalClose}
                    languageSelectorModalOpen={languageSelectorModalOpen}
                    onTranslateClick={onTranslateClick}
                    prepCheckWelcomeModalOpen={!!showWelcomeModal}
                    onPrepCheckWelcomeClose={onPrepCheckWelcomeClose}
                    onSignUpClick={onSignUpClick}
                    showPrepCheckLimitWarning={showPrepCheckLimitWarning}
                    onPrepCheckLimitWarningModalClose={
                        onPrepCheckLimitWarningModalClose
                    }
                />
            ) : null}
            <ZipCodeAlert
                open={showZipAlert}
                onClose={handleZipClose}
                onSubmit={handleZipSubmit}
                lang={language}
            />
        </>
    )
}
